import { geometry } from 'app/contracts/Geometry';
import { particles } from 'app/contracts/Particles';
import { ParticleBehaviourTagType, RepellType } from 'app/enum/Particles';
import { ParticleBehaviour } from '../ParticleBehaviour';

export class ParticleRepell extends ParticleBehaviour {
	protected _tags: Array<ParticleBehaviourTagType> = [ParticleBehaviourTagType.REPELL];

	public returnTo: geometry.Vector2;
	public returnPower = { x: 0.08, y: 0.08 };

	private _type: RepellType = RepellType.BOUNDARY;
	private _target: particles.ParticleTarget;
	private _radius: number;
	private _power: number;
	private _radians: number;
	private _center: geometry.Vector2;
	private _distance: number;
	private _counter: number = 0;
	private _testAtCount: number = 1;

	private _mode: string = 'POSITION';

	//GETTER / SETTER
	//--------------------------------------------------------------------------------------------------
	public set type(type: RepellType) {
		this._type = type;
	}

	public set testAtCount(testAtCount: number) {
		this._testAtCount = testAtCount;
	}

	public set target(target: particles.ParticleTarget) {
		this._target = target;
	}

	public set radius(radius: number) {
		this._radius = radius;
	}

	public set power(power: number) {
		this._power = power;
	}

	public reducePower(amount): void {
		this._power -= amount;
	}

	//PUBLIC METHODS
	//-----------------------------------------------------------------------------------------
	public randomiseRadius(base: number, seed: number): void {
		this._radius = base + Math.random() * seed;
	}

	public randomisePower(base: number, seed: number): void {
		this._power = base + Math.random() * seed;
	}

	public tick(): void {
		if (!this._active || !this._target || this._particleProps.sprite.visible === false) {
			return;
		}

		this._counter++;

		if (this._counter < this._testAtCount) {
			return;
		}
		this._counter = 0;

		this._center = this._target.position;


		//Add extras processing to push away items. Don't do this!
		//this._animatedProps.tick();



		let a, b;
		if (this._mode == 'POSITION') {
			a = this._animatedProps.x - this._center.x;
			b = this._animatedProps.y - this._center.y;
		} else {
			a = this._particleProps.ref.position.absX2 - this._center.x;
			b = this._particleProps.ref.position.absY - this._center.y;
		}

		this._distance = Math.sqrt(a * a + b * b);

		if (this._distance <= this._radius) {
			if (this._mode == 'POSITION') {
				this._radians = Math.atan2(this._animatedProps.y - this._center.y, this._animatedProps.x - this._center.x);
			} else {
				this._radians = Math.atan2(
					this._particleProps.ref.position.absY - this._center.y,
					this._particleProps.ref.position.absX2 - this._center.x
				);
			}

			//When boundary, then the repeel is harsh repell that instanly forces to edge
			//When push then it just sets the target (which animates the particle based on easing)

			if (this._type === RepellType.BOUNDARY) {
				//Constrain:
				this._animatedProps.x = this._center.x + Math.cos(this._radians) * this._power;
				this._animatedProps.y = this._center.y + Math.sin(this._radians) * this._power;
			} else if (this._type === RepellType.PUSH) {
				this._animatedProps.targetX = this._center.x + Math.cos(this._radians) * this._power;
				this._animatedProps.targetY = this._center.y + Math.sin(this._radians) * this._power;


				let r = Math.sin(this._distance / this._radius);


				let move = this._radius * r * 0.8;

				//Have 0-1
				//Want 0.5 - 1 - 0.5

				let r2 = this._distance / this._radius - 0.5; //Will be -0.5 to 0.5
				let r3 = 0.5 - Math.abs(r2);
				let r4 = r3 / 0.5;

				//this._distance 166.34744115287083 r2 0.33173720576435417 r3 0.16826279423564583 r4 0.33652558847129166

				//Need to be 1 when big


				move = 50 * r4;
				this._animatedProps.targetX = this._particleProps.ref.position.absX + Math.cos(this._radians) * move;
				this._animatedProps.targetY = this._particleProps.ref.position.absY + Math.sin(this._radians) * move;


				if (this._mode == 'ANCHOR') {
					a = this._animatedProps.targetX - this._center.x;
					b = this._animatedProps.targetY - this._center.y;
					this._distance = Math.sqrt(a * a + b * b);

					if (this._distance <= 50) {
						this._animatedProps.targetX = this._center.x + Math.cos(this._radians) * 50;
						this._animatedProps.targetY = this._center.y + Math.sin(this._radians) * 50;
					}
				}

			} else if (this._type === RepellType.POSITION) {
				this._particleProps.ref.position.x += Math.cos(this._radians) * this._power;
				this._particleProps.ref.position.y += Math.sin(this._radians) * (this._power * 3);
				this._animatedProps.targetScale = this._animatedProps.targetScaleSaved * 0.2;
			} else if (this._type === RepellType.VECTOR) {
				this._particleProps.ref.particlePhysics.repell(
					Math.cos(this._radians) * ((this._power / 2) * (2 * (window as any).deltaTime)),
					Math.sin(this._radians) * (this._power * 3 * (2 * (window as any).deltaTime))
				);
			}

		} else {
			if (this._animatedProps.targetScaleSaved) {
				this._animatedProps.targetScale = this._animatedProps.targetScaleSaved;
			}
		}

		if (this.returnTo && this._type === RepellType.POSITION) {
			this._particleProps.ref.position.x += (this.returnTo.x - this._particleProps.ref.position.x) * this.returnPower.x;
			this._particleProps.ref.position.y += (this.returnTo.y - this._particleProps.ref.position.y) * this.returnPower.y;

			return;
		}

		if (this._type === RepellType.BOUNDARY) {
			//Retarget:
			let targetDistance = Math.sqrt(
				Math.pow(this._animatedProps.targetX - this._center.x, 2) +
					Math.pow(this._animatedProps.targetY - this._center.y, 2)
			);

			if (targetDistance) {
				let targetRadians = Math.atan2(
					this._animatedProps.targetY - this._center.y,
					this._animatedProps.targetX - this._center.x
				);
				this._animatedProps.targetX = this._center.x + Math.cos(targetRadians) * this._power;
				this._animatedProps.targetY = this._center.y + Math.sin(targetRadians) * this._power;
			}
		}
	}

	//PRIVATE METHODS
	//-----------------------------------------------------------------------------------------
}
